import { useChatStore as useChatStoreCore } from '../chatStore'
import { ChatTransformer } from '../../services'
import { isChatListPreviewPending } from '@/modules/chat-matrix-fast/utils/chatListPreviewState'
import {
  CHAT_LIST_TAB,
  CHAT_LIST_TYPES,
  type ChatListTab,
  filterChatsByListTab,
  isEventChatItem,
  matchesChatListTab,
  mergeEventChatFields,
} from '../../constants/chat'
import type { ChatOut } from '../../types'
import { defineChatViewActions } from './actionsContext'
import {
  ensurePinnedGrouping,
  mergeContactsPreservingOrder,
  moveContactToUnpinnedTop,
  sortChatsByLastMessage,
} from './listHelpers'
import { debugConsoleError, eventsDebugLog, setChatDebugLane } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { logEventSidebarContacts } from '@/modules/chat-matrix-fast/utils/eventChatDebug'
import { useCurrentUser } from '@/modules/chat-matrix-fast/composables/useCurrentUser'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import {
  normalizeAvatarUrl,
  preferStableAvatar,
} from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { resolveMatrixSenderProfile } from '@/modules/chat-matrix-fast/services/matrixProfileCache'
import { isForgottenRoom } from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import {
  clearStoredSelectedChatId,
  setStoredChatListTab,
} from '@/modules/chat-matrix-fast/utils/chatListPersistence'
import avatar1 from '@images/avatars/avatar-1.png'
import type { IChat, IEmployee } from '@/modules/chat-matrix-fast/types/IChat'

export const listActions = defineChatViewActions({
  /** resort: первичная загрузка / pin / явный bump. Иначе порядок списка не трогаем (open не двигает чат). */
  rebuildChatsContactsFromCore(options?: {
    resort?: boolean
    bumpChatId?: number | string
    patchChatId?: number | string
  }): void {
    const chatStore = useChatStoreCore()

    if (chatStore.searchResults !== null) {
      return
    }

    const chatsForTab = filterChatsByListTab(chatStore.chats, this.chatListTab)
    const activeChatId = this.activeChat?.chat?.id
    const previousContacts = this.chatsContacts
    const previousForTab = previousContacts.filter(
      contact => matchesChatListTab(contact.chat, this.chatListTab),
    )
    const targetId = options?.bumpChatId ?? options?.patchChatId

    // Гидрация превью / bump чужой вкладки не должна пересобирать текущий список.
    if (targetId != null && previousContacts.length > 0) {
      const existsInPrevious = previousForTab.some(
        contact => String(contact.chat?.id ?? contact.id) === String(targetId),
      )
      const existsInTab = chatsForTab.some(chat => String(chat.id) === String(targetId))
      if (!existsInPrevious || !existsInTab) {
        return
      }
    }

    // Вкладка уже сменилась, список ещё от старой — ждём явный resort после fetch.
    if (
      previousContacts.length > 0
      && previousForTab.length === 0
      && options?.resort !== true
    ) {
      return
    }

    const hasPendingPreview = chatsForTab.some(chat => isChatListPreviewPending(chat.id))
    const shouldResort = options?.resort === true
      || (previousForTab.length === 0 && !hasPendingPreview)

    let contacts = ChatTransformer.transformChatsToContacts(
      chatsForTab,
      this.profileUser?.id,
    )

    if (shouldResort) {
      contacts = sortChatsByLastMessage(contacts)
    } else if (previousForTab.length > 0) {
      contacts = ensurePinnedGrouping(mergeContactsPreservingOrder(previousForTab, contacts))
    }

    if (options?.bumpChatId != null) {
      contacts = moveContactToUnpinnedTop(contacts, options.bumpChatId)
    }

    // Keep already-shown avatar when next is the same asset with a new signed query.
    const previousByChatId = new Map(
      previousContacts
        .filter(contact => contact.chat?.id != null)
        .map(contact => [contact.chat!.id, contact] as const),
    )
    contacts = contacts.map(contact => {
      const previous = contact.chat?.id != null
        ? previousByChatId.get(contact.chat.id)
        : undefined
      if (!previous?.avatar) {
        return contact
      }
      const stableAvatar = preferStableAvatar(previous.avatar, contact.avatar)
      const previousIcon = previous.chat?.icon
      const nextIcon = contact.chat?.icon
      const stableIcon = preferStableAvatar(previousIcon, nextIcon)
      const avatarChanged = Boolean(stableAvatar && stableAvatar !== contact.avatar)
      const iconChanged = Boolean(
        contact.chat
          && stableIcon
          && nextIcon
          && stableIcon !== nextIcon,
      )
      if (!avatarChanged && !iconChanged) {
        return contact
      }
      return {
        ...contact,
        ...(avatarChanged ? { avatar: stableAvatar } : {}),
        ...(iconChanged && contact.chat
          ? { chat: { ...contact.chat, icon: stableIcon } }
          : {}),
      }
    })

    if (activeChatId != null) {
      contacts = contacts.map(contact => {
        if (contact.chat?.id !== activeChatId) {
          return contact
        }

        const lastMessage = contact.chat.lastMessage
        return {
          ...contact,
          chat: {
            ...contact.chat,
            unseenMsgs: 0,
            ...(lastMessage
              ? {
                  lastMessage: {
                    ...lastMessage,
                    feedback: {
                      ...lastMessage.feedback,
                      isSeen: true,
                    },
                  },
                }
              : {}),
          },
        }
      })

      if (this.activeChat?.chat?.id === activeChatId) {
        this.activeChat.chat.unseenMsgs = 0
        if (this.activeChat.chat.lastMessage) {
          this.activeChat.chat.lastMessage.feedback.isSeen = true
        }
      }
    }

    this.chatsContacts = contacts
    this.contacts = []

    if (this.chatListTab === CHAT_LIST_TAB.EVENTS) {
      logEventSidebarContacts('sidebar · contacts rebuilt', contacts, {
        resort: shouldResort,
        bumpChatId: options?.bumpChatId ?? null,
      })
    }
  },

  async fetchChatsAndContacts(q: string) {
    const chatStore = useChatStoreCore()

    // Шапка (поиск / «+») не должна ждать Matrix — профиль ставим сразу из IAM.
    this.ensureProfileUser()

    if (!chatStore.isInitialized) {
      await chatStore.initializeOnce()
    }

    try {
      this.isLoading = true

      if (q.trim()) {
        const searchResults = await chatStore.searchChats(q.trim())

        const mergedDialogsMap = new Map<string, IEmployee>()
        for (const employee of searchResults.new_dialogs || []) {
          const id = String(employee.id)
          const existing = mergedDialogsMap.get(id)

          mergedDialogsMap.set(id, {
            ...existing,
            ...employee,
            id,
            phone_number: employee.phone_number || existing?.phone_number,
          })
        }

        const mergedNewDialogs = [...mergedDialogsMap.values()]

        const restChats = filterChatsByListTab(searchResults.chats, this.chatListTab)
        const localMatrixChats = filterChatsByListTab(
          chatStore.searchLocalChats(q.trim()).filter(
            chat => !chat.matrix_room_id || !isForgottenRoom(chat.matrix_room_id),
          ),
          this.chatListTab,
        )

        const mergedChatsMap = new Map<number, IChat>()
        for (const chat of [...restChats, ...localMatrixChats]) {
          mergedChatsMap.set(chat.id, chat)
        }

        const filteredChats = [...mergedChatsMap.values()]

        this.chatsContacts = sortChatsByLastMessage(
          ChatTransformer.transformChatsToContacts(
            filteredChats,
            this.profileUser?.id,
          ),
        )
        this.contacts = this.chatListTab === CHAT_LIST_TAB.EMPLOYEES
          ? ChatTransformer.transformEmployeesToContacts(mergedNewDialogs)
          : []
      } else {
        chatStore.searchResults = null

        this.rebuildChatsContactsFromCore()
      }
    } catch (error) {
      debugConsoleError('[chatViewStore] fetchChatsAndContacts:', error)
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось выполнить поиск',
        time: 5000,
      })
      this.rebuildChatsContactsFromCore()
    } finally {
      this.isLoading = false
    }

    void this.enrichProfileUserAvatarFromMatrix()
  },

  ensureProfileUser(): void {
    const currentUser = useCurrentUser()
    const iamAvatar = normalizeAvatarUrl(currentUser?.avatar?.value)
    const userAvatar = iamAvatar || this.profileUser?.avatar || avatar1

    this.profileUser = {
      id: currentUser?.id?.value || this.profileUser?.id || 'unknown',
      fullName: currentUser?.name?.value || this.profileUser?.fullName || 'Пользователь',
      role: this.profileUser?.role || 'Пользователь',
      about: this.profileUser?.about || 'Активный пользователь',
      avatar: userAvatar,
      online: true,
    }
  },

  async enrichProfileUserAvatarFromMatrix(): Promise<void> {
    const currentUser = useCurrentUser()
    const iamAvatar = normalizeAvatarUrl(currentUser?.avatar?.value)
    if (iamAvatar || !this.profileUser) {
      return
    }

    const matrixUserId = currentUser?.matrixUserId?.value
    if (!matrixUserId) {
      return
    }

    try {
      const matrixClient = await startMatrixClient()
      const profile = await resolveMatrixSenderProfile(matrixClient, matrixUserId)
      if (profile.avatarUrl && this.profileUser) {
        this.profileUser = {
          ...this.profileUser,
          avatar: normalizeAvatarUrl(profile.avatarUrl) || this.profileUser.avatar,
        }
      }
    } catch {
    }
  },

  async setChatListTab(tab: ChatListTab) {
    if (this.chatListTab === tab) {
      return
    }

    const chatStore = useChatStoreCore()

    if (
      chatStore.showTopicsList
        || chatStore.activeTopic
        || chatStore.selectedTopicsGroupId != null
    ) {
      chatStore.closeTopicsNavigation()
    }

    const activeChat = this.activeChat?.chat
    if (activeChat && !matchesChatListTab(activeChat, tab)) {
      clearStoredSelectedChatId(activeChat.id)
      this.activeChat = null
    }

    const currentChat = chatStore.currentChat
    if (currentChat && !matchesChatListTab(currentChat, tab)) {
      clearStoredSelectedChatId(currentChat.id)
      chatStore.currentChat = null
    }

    this.chatListTab = tab
    setStoredChatListTab(tab)
    this.chatsContacts = []
    this.contacts = []

    setChatDebugLane(tab === CHAT_LIST_TAB.EVENTS ? 'tasks' : 'chats')
    eventsDebugLog('tab · switch', {
      tab,
      apiTypes: CHAT_LIST_TYPES[tab],
    })

    await chatStore.fetchChats(CHAT_LIST_TYPES[tab])

    this.rebuildChatsContactsFromCore({ resort: true })
    await this.fetchChatsAndContacts('')
    await this.refreshActiveEventChatIfNeeded()
    this.syncWithCoreStore()
  },

  async refreshActiveEventChatIfNeeded(): Promise<void> {
    const chatId = this.activeChat?.chat?.id
    if (!chatId || this.chatListTab !== CHAT_LIST_TAB.EVENTS) {
      return
    }

    const previousActiveChat = this.activeChat?.chat as IChat | undefined
    if (!isEventChatItem(previousActiveChat)) {
      return
    }

    const chatStore = useChatStoreCore()
    const hasEventUrl = Boolean(previousActiveChat?.event_url)

    if (hasEventUrl) {
      this.activeChat!.chat = mergeEventChatFields(
        this.activeChat!.chat as IChat,
        previousActiveChat,
        chatStore.currentChat ?? undefined,
      ) as ChatOut

      return
    }

    try {
      const detailedChat = await chatStore.fetchChat(chatId)
      const mergedChat = mergeEventChatFields(
        detailedChat,
        previousActiveChat,
        chatStore.currentChat ?? undefined,
      )

      const chatIndex = chatStore.chats.findIndex(c => c.id === chatId)
      if (chatIndex !== -1) {
        chatStore.chats[chatIndex] = mergeEventChatFields(chatStore.chats[chatIndex], mergedChat)
      }

      if (chatStore.currentChat?.id === chatId) {
        chatStore.currentChat = mergeEventChatFields(chatStore.currentChat, mergedChat)
      }

      this.updateActiveChat(mergedChat, chatId)
    } catch (error) {
      debugConsoleError('[chatViewStore] refreshActiveEventChatIfNeeded:', error)
    }
  },

  async ensureChatListTabForChat(chat: IChat) {
    const tab = isEventChatItem(chat)
      ? CHAT_LIST_TAB.EVENTS
      : CHAT_LIST_TAB.EMPLOYEES

    if (this.chatListTab !== tab) {
      await this.setChatListTab(tab)
    }
  },
})
